import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/api/axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Brain, PaperPlaneRight, Plus, Trash, Sparkle, ChatCircleDots, User, Robot } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SUGGESTIONS = [
  '¿Cómo vamos hoy?',
  '¿Cómo vamos este mes vs mes anterior?',
  '¿Quién va arriba y abajo de meta?',
  '¿Qué sucursal está creciendo?',
  '¿Cuál es la proyección de cierre del mes?',
  '¿Cuáles son los productos más vendidos?',
  '¿Cuáles son mis top 5 clientes?',
  '¿Qué cotizaciones están pendientes y por cuánto?',
  '¿Qué clientes llevan más de 90 días sin comprar?',
  '¿Qué productos están críticos de stock?',
];

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short' });
};

// Naive markdown-ish renderer (bold + bullet points)
const renderMessage = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, i) => {
    // Bold replacement: **text** → <strong>
    const html = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
      return <li key={i} dangerouslySetInnerHTML={{ __html: html.replace(/^[-•]\s*/, '') }} className="ml-4" />;
    }
    if (line.trim() === '') return <div key={i} className="h-2" />;
    return <p key={i} dangerouslySetInnerHTML={{ __html: html }} />;
  });
};

export default function AsistenteIA() {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get('/ai/asistente/sessions');
      setSessions(data || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error('Solo Admin/Supervisor/Gerente pueden usar el Asistente');
    }
  }, []);

  const loadMessages = useCallback(async (sid) => {
    try {
      const { data } = await api.get(`/ai/asistente/sessions/${sid}/messages`);
      setMessages(data || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { if (activeSession) loadMessages(activeSession); else setMessages([]); }, [activeSession, loadMessages]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput('');
    // Optimistic add
    setMessages(m => [...m, { id: `tmp-${Date.now()}`, role: 'user', content: msg, created_at: new Date().toISOString() }]);
    setLoading(true);
    try {
      const { data } = await api.post('/ai/asistente/chat', { message: msg, session_id: activeSession });
      if (!activeSession) setActiveSession(data.session_id);
      setMessages(m => [...m, { id: `r-${Date.now()}`, role: 'assistant', content: data.message, created_at: new Date().toISOString() }]);
      loadSessions();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Error al consultar el asistente';
      toast.error(detail);
      setMessages(m => [...m, { id: `e-${Date.now()}`, role: 'assistant', content: `⚠️ ${detail}`, created_at: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setActiveSession(null);
    setMessages([]);
    setInput('');
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta conversación?')) return;
    try {
      await api.delete(`/ai/asistente/sessions/${sid}`);
      if (activeSession === sid) startNew();
      loadSessions();
    } catch { /* silent */ }
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]" data-testid="asistente-ia-page">
      {/* Sidebar: sessions */}
      <Card className={`w-72 border-r border-[var(--border)] rounded-none flex flex-col bg-[var(--surface)] ${activeSession || messages.length > 0 ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-3 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="text-xs font-black uppercase tracking-wider text-[var(--text)] flex items-center gap-2">
            <Brain size={14} weight="duotone" className="text-purple-600" /> Conversaciones
          </h2>
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white h-7 px-2" onClick={startNew} data-testid="new-session-btn">
            <Plus size={12} /> Nueva
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-1">
          {sessions.length === 0 ? (
            <p className="text-[11px] text-[var(--text-muted)] italic p-3 text-center">Sin conversaciones aún</p>
          ) : sessions.map(s => (
            <div
              key={s.id}
              onClick={() => setActiveSession(s.id)}
              className={`group flex items-start justify-between gap-2 p-2 rounded cursor-pointer mb-1 ${activeSession === s.id ? 'bg-purple-100 dark:bg-purple-900/30' : 'hover:bg-[var(--surface-muted)]'}`}
              data-testid={`session-${s.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-[var(--text)] truncate">{s.title || 'Sin título'}</div>
                <div className="text-[10px] text-[var(--text-muted)]">{fmtTime(s.updated_at)} · {s.message_count || 0} msg</div>
              </div>
              <button onClick={(e) => deleteSession(s.id, e)} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition">
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* Chat panel */}
      <div className="flex-1 flex flex-col bg-[var(--bg)]">
        <div className="p-3 border-b border-[var(--border)] bg-[var(--surface)] flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-500 flex items-center justify-center">
            <Brain size={18} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-base font-black text-[var(--text)]">Asistente Ejecutivo</h1>
            <p className="text-[10px] text-[var(--text-muted)]">Grupo Sach · GPT-5.2 con datos del CRM en vivo</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-4xl mx-auto w-full">
          {messages.length === 0 ? (
            <div className="space-y-4">
              <div className="text-center py-6">
                <Sparkle size={36} weight="duotone" className="mx-auto text-purple-500 mb-2" />
                <h2 className="text-lg font-bold text-[var(--text)]">¡Hola! Soy tu asistente ejecutivo.</h2>
                <p className="text-xs text-[var(--text-muted)] mt-1">Pregúntame lo que quieras sobre el negocio. Respondo con números reales del CRM.</p>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-2">💡 Sugerencias para empezar:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      className="text-left text-xs p-3 rounded-lg border border-[var(--border)] bg-[var(--surface)] hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition"
                      data-testid={`suggestion-${i}`}
                    >
                      <ChatCircleDots size={12} className="inline mr-1 text-purple-500" /> {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            messages.map(m => (
              <div key={m.id} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                    <Robot size={14} weight="fill" className="text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === 'user' ? 'bg-purple-600 text-white rounded-br-sm' : 'bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-bl-sm'}`}>
                  <div className="space-y-1">{renderMessage(m.content)}</div>
                </div>
                {m.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
                    <User size={14} weight="bold" className="text-white" />
                  </div>
                )}
              </div>
            ))
          )}
          {loading && (
            <div className="flex gap-2 justify-start">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <Robot size={14} weight="fill" className="text-white animate-pulse" />
              </div>
              <div className="px-4 py-3 rounded-2xl bg-[var(--surface)] border border-[var(--border)]">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="p-3 pb-24 md:pb-3 border-t border-[var(--border)] bg-[var(--surface)] max-w-4xl mx-auto w-full">
          <div className="flex items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Escribe tu pregunta… (Enter para enviar)"
              rows={1}
              disabled={loading}
              className="flex-1 resize-none min-h-[44px] max-h-32 text-sm"
              data-testid="ia-input"
            />
            <Button onClick={() => send()} disabled={loading || !input.trim()} className="bg-purple-600 hover:bg-purple-700 text-white h-11" data-testid="ia-send-btn">
              <PaperPlaneRight size={16} weight="fill" />
            </Button>
          </div>
          <p className="text-[10px] text-[var(--text-faint)] mt-1 text-center">
            Las respuestas usan los datos del CRM en vivo + HarpERP. Si un dato no está, te lo avisará.
          </p>
        </div>
      </div>
    </div>
  );
}
